import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig, seoMeta } from '@/mocks/siteConfig';
import { categories } from '@/mocks/categories';
import { comics, getFeaturedComics, getHotComics, getNewReleases } from '@/mocks/comics';

export default function Home() {
  const featuredComics = getFeaturedComics();
  const hotComics = getHotComics();
  const newReleases = getNewReleases();

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    logo: `${siteConfig.url}/logo.png`,
    sameAs: [siteConfig.social.siteLink],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      'target': `${siteConfig.url}/category?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seoMeta.home.title,
    description: seoMeta.home.description,
    url: siteConfig.url,
    isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫阁是什么网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫阁是一个提供免费无遮韩国漫画在线阅读的平台，支持下拉式阅读体验。网站整合了丰富的漫画韩漫资源，涵盖热血、恋爱、奇幻、都市、悬疑等多种类型，致力于打造无障碍的漫画阅读环境。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫阁的漫画是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫阁所有漫画均为免费阅读，无需付费订阅。我们提供无遮漫画下拉式阅读服务，致力于让每一位漫画爱好者都能无障碍地享受韩国漫画。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在韩漫阁上阅读漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '只需访问韩漫阁网站，在首页浏览推荐作品或通过分类页面找到喜欢的漫画，点击即可进入阅读页面。我们采用下拉式阅读方式，适配手机和电脑，操作简单流畅。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫阁有哪些漫画类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫阁涵盖热血漫画、恋爱漫画、奇幻漫画、都市漫画、悬疑漫画、搞笑漫画、恐怖漫画、古风漫画、BL漫画等多种类型，同时还设有完结漫画专区，满足不同读者的阅读偏好。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫阁每天更新漫画吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫阁每日更新海量韩国漫画内容。我们持续追踪最新的韩漫资源，确保读者能够第一时间阅读到最新的漫画章节，热门作品保持高频更新。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={seoMeta.home.title}
        description={seoMeta.home.description}
        keywords={seoMeta.home.keywords}
        canonicalPath="/"
        ogImage="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20reading%20website%20hero%20banner%20with%20colorful%20comic%20book%20illustrations%2C%20warm%20and%20inviting%20atmosphere%2C%20modern%20web%20design%2C%20character%20art%20showcase%2C%20clean%20professional%20look&width=1200&height=630&seq=home-og-image&orientation=landscape"
        schemaJson={{ ...organizationSchema, ...websiteSchema, ...webpageSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20hero%20banner%20for%20a%20Korean%20manhwa%20website%2C%20abstract%20artistic%20colorful%20ink%20splashes%20with%20warm%20coral%20orange%20and%20mint%20green%20accents%20on%20dark%20charcoal%20background%2C%20modern%20geometric%20shapes%2C%20clean%20minimalist%20aesthetic%2C%20no%20text%2C%20smooth%20gradients&width=1600&height=900&seq=home-hero-bg&orientation=landscape"
              alt="韩漫阁-韩国漫画网"
              title="韩漫阁-无遮韩国免费漫画大全"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/80"></div>
          <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-6 text-center py-20">
            <p className="text-primary-400 text-sm md:text-base font-medium tracking-wider mb-4">韩国漫画网 · 无遮免费漫画大全</p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              韩国漫画网
              <br />
              <span className="text-primary-400">无遮韩国免费漫画大全</span>
            </h1>
            <p className="text-background-50/70 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              提供免费无遮漫画下拉式服务，整合漫画韩漫网址，支持韩漫在线阅读，致力于打造无障碍的漫画阅读环境
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category"
                className="bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                开始阅读
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="text-background-50/80 px-8 py-3 rounded-full text-sm font-medium border border-background-50/30 hover:border-background-50/60 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>

            {/* 3D perspective cards */}
            <div className="hidden md:flex items-end justify-center gap-3 mt-14">
              {featuredComics.slice(0, 5).map((comic, idx) => {
                const offsets = [-2, -1, 0, 1, 2];
                const translateY = Math.abs(offsets[idx]) * 16;
                const rotate = offsets[idx] * 3;
                return (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="block flex-shrink-0 transition-all duration-300 hover:translate-y-[-12px] cursor-pointer"
                    style={{
                      transform: `translateY(${translateY}px) rotate(${rotate}deg)`,
                      zIndex: idx === 2 ? 10 : 5 - Math.abs(offsets[idx]),
                    }}
                  >
                    <div className="w-[140px] md:w-[160px] aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - 韩国漫画免费在线阅读`}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </header>

        {/* Platform Features */}
        <section className="bg-secondary-100/50 py-16 md:py-20">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 mb-12">
              <div className="md:w-1/3">
                <p className="text-xs text-foreground-500 uppercase tracking-widest font-medium mb-2">{siteConfig.name}</p>
                <h2 className="font-heading text-foreground-950 text-2xl md:text-3xl font-bold">为什么选择<br />韩漫阁？</h2>
              </div>
              <div className="md:w-2/3">
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  韩漫阁是国内领先的韩国漫画在线阅读平台，提供免费、无遮、下拉式的优质漫画阅读体验。我们持续收录最新最热的韩漫资源，让每一位漫画爱好者都能畅享无障碍的阅读乐趣。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-foreground-950 rounded-xl p-6 text-background-50 group hover:bg-foreground-900 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-foreground-800 mb-5 group-hover:bg-primary-500 transition-colors">
                  <i className="ri-book-open-line text-xl text-primary-400 group-hover:text-background-50 transition-colors"></i>
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">海量漫画资源</h3>
                <p className="text-foreground-400 text-sm leading-relaxed">
                  收录超过10000部韩国漫画，涵盖热血、恋爱、奇幻、都市等多元类型。每日持续更新，让您永远不缺好漫画可看。
                </p>
              </div>

              <div className="relative rounded-xl overflow-hidden group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20person%20comfortably%20reading%20Korean%20manhwa%20on%20a%20smartphone%20in%20a%20cozy%20setting%2C%20warm%20ambient%20lighting%2C%20modern%20lifestyle%2C%20clean%20aesthetic%2C%20soft%20shadows&width=600&height=400&seq=home-feature-reading&orientation=landscape"
                  alt="下拉式阅读体验"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-foreground-950/60 text-background-50 text-xs px-3 py-1 rounded-full border border-background-50/20 whitespace-nowrap">
                    <i className="ri-smartphone-line mr-1"></i>下拉式阅读
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-heading text-lg font-bold text-background-50 mb-1">流畅下拉式体验</h3>
                  <p className="text-background-50/70 text-xs">专为手机优化的竖屏下拉式阅读，一滑到底</p>
                </div>
              </div>

              <div className="flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">免费无遮漫画</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                    全部漫画免费阅读，无任何遮挡和删减。我们致力于为读者提供最纯粹、最完整的韩漫阅读体验。
                  </p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-accent-600 font-semibold">
                    <i className="ri-checkbox-circle-fill"></i> 100%免费
                  </span>
                  <span className="flex items-center gap-1 text-accent-600 font-semibold">
                    <i className="ri-checkbox-circle-fill"></i> 无广告打扰
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">漫画分类</h2>
                <p className="text-foreground-500 text-sm">探索你喜欢的漫画类型</p>
              </div>
              <Link to="/category" className="text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1">
                查看全部分类 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {categories.slice(0, 10).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?cat=${cat.slug}`}
                  className="group bg-background-100 hover:bg-primary-50 rounded-xl p-5 text-center transition-all cursor-pointer border border-background-200/70 hover:border-primary-200"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 mx-auto mb-3 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl text-foreground-600 group-hover:text-primary-500 transition-colors`}></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500">{cat.count}+部</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-16 md:py-20 bg-foreground-950">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-10">
              <div>
                <Link
                  to="/category"
                  className="inline-block border border-background-50/30 text-background-50/80 px-4 py-1 rounded-full text-xs font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap mb-4"
                >
                  热门推荐
                </Link>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50">大家都在看</h2>
              </div>
              <Link to="/category" className="text-primary-400 text-sm font-medium hover:text-primary-300 transition-colors whitespace-nowrap flex items-center gap-1">
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">最新更新</h2>
                <p className="text-foreground-500 text-sm">刚更新的热门漫画，别错过</p>
              </div>
              <Link to="/category" className="text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
              {newReleases.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="default" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20abstract%20artistic%20background%20with%20soft%20coral%20orange%20and%20cream%20tones%2C%20gentle%20gradients%2C%20smooth%20flowing%20shapes%2C%20modern%20minimalist%20style%2C%20no%20text%2C%20clean%20elegant%20composition&width=1600&height=800&seq=home-cta-bg&orientation=landscape"
              alt="开启韩漫阅读之旅"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 bg-foreground-950/50"></div>
          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">立即开启你的韩漫阅读之旅</h2>
            <p className="text-background-50/70 text-sm md:text-base mb-8 max-w-xl mx-auto">
              加入数百万读者的行列，在韩漫阁发现属于你的下一部心头好
            </p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-foreground-900 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center bg-primary-500 rounded-full">
                <i className="ri-book-open-line text-[10px] text-background-50"></i>
              </span>
              免费开始阅读
              <i className="ri-arrow-right-up-line ml-1"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}